import { useLayoutEffect, useRef } from 'react';

interface NumberInputProps {
  value: string;
  onChange: (digits: string) => void;
  suffix?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  id?: string;
  name?: string;
  ariaLabel?: string;
  size?: 'sm' | 'md';
}

function formatDigitsWithSpaces(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function stripNonDigits(s: string): string {
  return s.replace(/\D/g, '');
}

function countDigitsBefore(formatted: string, caretIndex: number): number {
  let count = 0;
  const end = Math.min(caretIndex, formatted.length);
  for (let i = 0; i < end; i++) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      count++;
    }
  }
  return count;
}

function caretIndexAfterNDigits(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      count++;
      if (count === digitCount) {
        return i + 1;
      }
    }
  }
  return formatted.length;
}

const WRAPPER_CLASSES_BY_SIZE = {
  md:
    'inline-flex items-stretch w-48 rounded-lg border border-gray-300 dark:border-gray-600 ' +
    'bg-white dark:bg-gray-700 overflow-hidden ' +
    'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
  sm:
    'inline-flex items-stretch w-36 rounded-lg border border-gray-300 dark:border-gray-600 ' +
    'bg-white dark:bg-gray-700 overflow-hidden ' +
    'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
};

const INPUT_CLASSES_BY_SIZE = {
  md:
    'flex-1 min-w-0 px-3 py-2 bg-transparent text-gray-900 dark:text-white ' +
    'text-lg font-bold text-left ' +
    'focus:outline-none focus:ring-0 ' +
    'placeholder-gray-400 dark:placeholder-gray-500 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
  sm:
    'flex-1 min-w-0 px-3 py-2 bg-transparent text-gray-900 dark:text-white ' +
    'text-sm font-semibold text-left ' +
    'focus:outline-none focus:ring-0 ' +
    'placeholder-gray-400 dark:placeholder-gray-500 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
};

const SUFFIX_CLASSES_BY_SIZE = {
  md:
    'flex items-center px-3 text-lg font-medium ' +
    'text-gray-500 dark:text-gray-400 ' +
    'bg-gray-50 dark:bg-gray-800 ' +
    'border-l border-gray-300 dark:border-gray-600 ' +
    'select-none',
  sm:
    'flex items-center px-3 text-sm font-medium ' +
    'text-gray-500 dark:text-gray-400 ' +
    'bg-gray-50 dark:bg-gray-800 ' +
    'border-l border-gray-300 dark:border-gray-600 ' +
    'select-none',
};

export default function NumberInput({
  value,
  onChange,
  suffix,
  placeholder,
  disabled = false,
  className,
  inputClassName,
  id,
  name,
  ariaLabel,
  size = 'md',
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaretDigitsRef = useRef<number | null>(null);

  const displayed = formatDigitsWithSpaces(value);

  useLayoutEffect(() => {
    if (pendingCaretDigitsRef.current !== null && inputRef.current !== null) {
      const idx = caretIndexAfterNDigits(displayed, pendingCaretDigitsRef.current);
      inputRef.current.setSelectionRange(idx, idx);
      pendingCaretDigitsRef.current = null;
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Skip cursor handling during IME composition
    if (e.nativeEvent instanceof InputEvent && e.nativeEvent.isComposing) {
      onChange(stripNonDigits(e.target.value));
      return;
    }
    const rawNew = e.target.value;
    const caret = e.target.selectionStart ?? rawNew.length;
    const digitsBeforeCaret = countDigitsBefore(rawNew, caret);
    const nextDigits = stripNonDigits(rawNew);
    pendingCaretDigitsRef.current = digitsBeforeCaret;
    onChange(nextDigits);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const selStart = input.selectionStart;
    const selEnd = input.selectionEnd;
    if (selStart === null || selEnd === null || selStart !== selEnd) {
      return; // only special-case no-selection cases
    }
    const caret = selStart;

    if (e.key === 'Backspace' && caret > 0 && displayed[caret - 1] === ' ') {
      e.preventDefault();
      const digitsBeforeCaret = countDigitsBefore(displayed, caret);
      if (digitsBeforeCaret === 0) return;
      const digitToRemove = digitsBeforeCaret - 1;
      const nextDigits = value.slice(0, digitToRemove) + value.slice(digitToRemove + 1);
      pendingCaretDigitsRef.current = digitToRemove;
      onChange(nextDigits);
      return;
    }

    if (e.key === 'Delete' && caret < displayed.length && displayed[caret] === ' ') {
      e.preventDefault();
      const digitsBeforeCaret = countDigitsBefore(displayed, caret);
      const digitToRemove = digitsBeforeCaret; // the digit right after the space
      if (digitToRemove >= value.length) return;
      const nextDigits = value.slice(0, digitToRemove) + value.slice(digitToRemove + 1);
      pendingCaretDigitsRef.current = digitsBeforeCaret;
      onChange(nextDigits);
      return;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const input = e.currentTarget;
    const selStart = input.selectionStart ?? displayed.length;
    const selEnd = input.selectionEnd ?? selStart;
    const pastedDigits = stripNonDigits(e.clipboardData.getData('text'));
    const digitsBeforeStart = countDigitsBefore(displayed, selStart);
    const digitsBeforeEnd = countDigitsBefore(displayed, selEnd);
    const nextDigits =
      value.slice(0, digitsBeforeStart) + pastedDigits + value.slice(digitsBeforeEnd);
    pendingCaretDigitsRef.current = digitsBeforeStart + pastedDigits.length;
    onChange(nextDigits);
  };

  const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const selStart = input.selectionStart;
    const selEnd = input.selectionEnd;
    if (selStart === null || selEnd === null || selStart === selEnd) {
      return; // nothing selected, let default run (copies nothing anyway)
    }
    const selected = displayed.slice(selStart, selEnd);
    e.clipboardData.setData('text/plain', stripNonDigits(selected));
    e.preventDefault();
  };

  const handleCut = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const selStart = input.selectionStart;
    const selEnd = input.selectionEnd;
    if (selStart === null || selEnd === null || selStart === selEnd) {
      return;
    }
    const selected = displayed.slice(selStart, selEnd);
    e.clipboardData.setData('text/plain', stripNonDigits(selected));
    const digitsBeforeStart = countDigitsBefore(displayed, selStart);
    const digitsBeforeEnd = countDigitsBefore(displayed, selEnd);
    const nextDigits = value.slice(0, digitsBeforeStart) + value.slice(digitsBeforeEnd);
    pendingCaretDigitsRef.current = digitsBeforeStart;
    onChange(nextDigits);
    e.preventDefault();
  };

  const wrapperClasses = className ?? WRAPPER_CLASSES_BY_SIZE[size];
  const inputClasses = inputClassName ?? INPUT_CLASSES_BY_SIZE[size];
  const suffixClasses = SUFFIX_CLASSES_BY_SIZE[size];

  return (
    <div className={wrapperClasses}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        pattern="[0-9 ]*"
        autoComplete="off"
        value={displayed}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCopy={handleCopy}
        onCut={handleCut}
        className={inputClasses}
      />
      {suffix !== undefined && (
        <span aria-hidden="true" className={suffixClasses}>
          {suffix}
        </span>
      )}
    </div>
  );
}
